import type { ReactNode, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Menu, Search, Globe, Sparkles, LogOut, ExternalLink, ChevronDown, Check } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useSearchStore, useSelectedPropertyStore } from '@/stores'
import { useProperties, useAllProperties } from '@/hooks/useProperties'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { buildResumeParams } from '@/lib/onboardingResume'
import { Button } from '@/components/ui/Button'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { isIosApp } from '@/lib/platform'

export interface HeaderProps {
  onMenuClick?: () => void
  actions?: ReactNode
  className?: string
}

export function Header({ onMenuClick, actions, className = '' }: HeaderProps) {
  const { user, logout } = useAuth()
  const { i18n, t } = useTranslation('common')
  const openSearch = useSearchStore((s) => s.open)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const { data: propertiesData } = useProperties(1)
  const propertyList = propertiesData?.data ?? []
  // D3: validation source for the effect below, kept separate from
  // `propertyList` above. The dropdown itself stays on page 1 (20 rows); this
  // is the up-to-50 flat list so a selection reached via a direct URL beyond
  // page 1 is not mistaken for stale and cleared out from under the user.
  const { data: allProperties } = useAllProperties()
  const location = useLocation()
  const navigate = useNavigate()
  // INV-2: App.tsx wraps /settings in <AdminRoute>, which bounces staff back to
  // "/" — so the dropdown must not offer a Settings link staff can't reach.
  // Matches Sidebar's isAdmin gate for the same route.
  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  // EP-150 (App Review 2.1b): the iOS app carries no link to the pricing page.
  const showPricingLink = !isIosApp()
  // Settings and Pricing sit between two dividers. A staff member on the iOS
  // app has neither, and the two dividers would meet with nothing between.
  const hasMenuLinks = isAdmin || showPricingLink

  // The top-right avatar menu opens a DaisyUI dropdown on desktop but a bottom
  // action sheet on mobile (owner request 2026-07-27). Mirrors the Select
  // pattern: the sheet is only ever opened by the `sm:hidden` mobile trigger, so
  // on desktop `userSheetOpen` stays false and never engages its scroll-lock.
  const [userSheetOpen, setUserSheetOpen] = useState(false)
  const closeUserSheet = () => setUserSheetOpen(false)

  // Keep the store in sync with the URL on property-scoped routes
  // (`/properties/:id/...` — parking/*, readings, common-area-fees/*). Those
  // pages read the property from the URL param, not the store, so without this
  // the header switcher would show a stale selection after a deep link / reload.
  useEffect(() => {
    const segs = location.pathname.split('/')
    if (segs[1] === 'properties' && /^\d+$/.test(segs[2] ?? '')) {
      const urlId = Number(segs[2])
      if (urlId !== selectedPropertyId) setSelectedPropertyId(urlId)
    }
  }, [location.pathname, selectedPropertyId, setSelectedPropertyId])

  // D3, central validation: a persisted selection can outlive the property it
  // points at (deleted property, or restored from a different account before
  // syncAccount ran). Once the up-to-50 list has loaded, clear a selection
  // that isn't in it. Skipped on a `/properties/:id/...` route, because the effect
  // above owns writing the store from the URL there, and must get the first
  // chance to set it before this effect judges the result.
  useEffect(() => {
    if (!allProperties) return
    const segs = location.pathname.split('/')
    if (segs[1] === 'properties' && /^\d+$/.test(segs[2] ?? '')) return
    if (selectedPropertyId != null && !allProperties.some((p) => p.id === selectedPropertyId)) {
      setSelectedPropertyId(null)
    }
  }, [allProperties, location.pathname, selectedPropertyId, setSelectedPropertyId])

  // Section-aware property switch. On a property-scoped route the page reads
  // `:propertyId` from the URL (the store is inert there), so we must navigate:
  // rewrite `:id` to the picked property and keep the *section* path segments
  // (e.g. `parking/lpr`, `readings`), dropping any per-entity id / `new` / `edit`
  // so we land on that section's list under the new property. Off those routes
  // the store write alone is enough.
  const handlePropertyChange = (raw: string) => {
    const id = raw ? Number(raw) : null
    setSelectedPropertyId(id)
    if (id == null) return
    const segs = location.pathname.split('/')
    const onPropertyRoute = segs[1] === 'properties' && /^\d+$/.test(segs[2] ?? '')
    if (!onPropertyRoute) return
    const kept: string[] = []
    for (const s of segs.slice(3)) {
      if (/^\d+$/.test(s) || s === 'new' || s === 'edit') break
      kept.push(s)
    }
    navigate(`/properties/${id}${kept.length ? '/' + kept.join('/') : ''}`)
  }

  // Resume-setup badge: visible only while onboarding is not yet completed.
  // Once the user finishes (or marks complete from Settings), the badge
  // disappears. Clicking jumps them back to whatever step they left at AND
  // carries every upstream entity ID (property/customer/lease/invoice) so
  // the wizard's missing-upstream-entity guard doesn't bounce them
  // backwards (e.g. step 5 with no customer → step 4).
  const { data: accountSettings } = useAccountSettings()
  const onboardingCompleted = accountSettings?.settings?.onboarding_completed === true
  const showResumeBadge = accountSettings != null && !onboardingCompleted
  // Derive the resume step from entity counts (not onboarding_step) so a user
  // who browsed/exited without saving is still sent to the furthest reachable
  // step rather than always being bounced back to step 1.
  const resumeParams = buildResumeParams(accountSettings)
  const resumeHref = `/onboarding?${resumeParams.toString()}`

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'th' : 'en'
    i18n.changeLanguage(newLang)
    localStorage.setItem('language', newLang)
  }

  const avatarInner = (
    <div className="w-10 rounded-full bg-primary/10 flex items-center justify-center">
      <span className="text-primary text-sm font-medium">
        {(user?.full_name || user?.email)?.charAt(0).toUpperCase() || 'U'}
      </span>
    </div>
  )

  return (
    <header className={`min-h-16 bg-base-100 border-b border-base-300 px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-5 flex items-center gap-4 ${className}`}>
      {/* Mobile menu button */}
      {onMenuClick && (
        <Button
          variant="ghost"
          shape="square"
          onClick={onMenuClick}
          className="lg:hidden"
          aria-label={t('labels.open_menu')}
          data-testid="mobile-menu-button"
        >
          <Menu className="w-5 h-5" />
        </Button>
      )}

      {/* Search — `min-w-0` lets the flex item shrink below the intrinsic
          width of its child so the placeholder doesn't blow the button to
          two lines on dense headers (Resume-setup badge + property switcher
          + lang + avatar all compete for room on the right). The text span
          truncates with ellipsis instead. */}
      <div className="hidden sm:block flex-1 min-w-0 max-w-md">
        <button
          onClick={openSearch}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-base-300 bg-base-200/50 text-base-content/40 hover:bg-base-200 transition-colors text-sm"
        >
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 min-w-0 text-left truncate">{t('search.placeholder')}</span>
          <kbd className="kbd kbd-sm shrink-0">{t('search.keyboard_hint')}</kbd>
        </button>
      </div>
      {/* Mobile: search icon-only — full button is hidden on <sm above */}
      <Button
        variant="ghost"
        shape="square"
        onClick={openSearch}
        className="sm:hidden"
        aria-label={t('search.placeholder')}
      >
        <Search className="w-5 h-5" />
      </Button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Resume-setup badge — only when onboarding wasn't completed.
          Sits left of the language toggle so it's prominent but not crowding
          the avatar. Persists across pages so a user who skipped from any
          surface can always get back. */}
      {showResumeBadge && (
        <Button
          as={Link}
          to={resumeHref}
          data-testid="resume-onboarding-badge"
          title={t('header.resume_onboarding_title')}
          size="sm"
          leftIcon={<Sparkles className="w-4 h-4" />}
          className="hidden md:inline-flex"
        >
          {t('header.resume_onboarding')}
        </Button>
      )}

      {/* Actions */}
      {actions}

      {/* Property switcher — persisted in localStorage via the
          useSelectedPropertyStore. Hidden when no properties are loaded yet.
          TC-MUI-A5: hidden on mobile (top bar is cramped). On phones the
          property selector lives in each list page's PageHeader top-right
          (`titleAction`); the top-bar switcher is desktop-only (`sm`+). */}
      {/* Not on Reports or the dashboard: they keep their own building choice
          and open on all buildings (owner, 2026-10-01 and 2026-10-05), so this
          switcher would change nothing there. */}
      {propertyList.length > 0 && !location.pathname.startsWith('/reports') && location.pathname !== '/' && (
        <PropertyPickerListbox
          ariaLabel={t('labels.select_property')}
          value={selectedPropertyId ? String(selectedPropertyId) : ''}
          onChange={handlePropertyChange}
          className="hidden sm:block w-44"
          options={[
            // MON-18 C: the empty option used to repeat the prompt text
            // ("Select property"), which read as an unfilled field rather
            // than a real choice. It names what it actually shows: every
            // building.
            { value: '', label: t('labels.all_properties') },
            ...propertyList.map((p) => ({ value: String(p.id), label: p.name })),
          ]}
        />
      )}

      {/* Language toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleLanguage}
        title={t('actions.switch_language')}
        leftIcon={<Globe className="w-4 h-4" />}
      >
        <span className="text-xs font-medium">{i18n.language.toUpperCase()}</span>
      </Button>

      {/* User menu — desktop: DaisyUI dropdown (sm+ only). */}
      <div className="dropdown dropdown-end hidden sm:block">
        <Button
          variant="ghost"
          shape="circle"
          aria-haspopup="menu"
          aria-label={t('labels.user_menu')}
          className="avatar placeholder"
        >
          {avatarInner}
        </Button>
        <ul
          tabIndex={0}
          role="menu"
          className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-52 mt-2 border border-base-300"
        >
          <li className="menu-title px-4 py-2">
            <span className="text-xs text-base-content/60">{t('header.signed_in_as')}</span>
            <span className="font-medium truncate">{user?.email}</span>
          </li>
          <div className="divider my-0"></div>
          {isAdmin && (
            <li>
              <Link to="/settings" role="menuitem">{t('nav.settings')}</Link>
            </li>
          )}
          {showPricingLink && (
            <li>
              <a
                href="https://flowrent.io/pricing"
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
                className="flex items-center gap-2"
              >
                <span className="flex-1">{t('header.pricing_link')}</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </li>
          )}
          {hasMenuLinks && <div className="divider my-0" />}
          <li>
            <button
              type="button"
              onClick={logout}
              role="menuitem"
              className="text-error flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t('actions.logout')}
            </button>
          </li>
        </ul>
      </div>

      {/* User menu — mobile: the avatar opens a bottom action sheet (sm:hidden). */}
      <Button
        variant="ghost"
        shape="circle"
        aria-haspopup="menu"
        aria-expanded={userSheetOpen}
        aria-label={t('labels.user_menu')}
        onClick={() => setUserSheetOpen(true)}
        className="avatar placeholder sm:hidden"
        data-testid="user-menu-mobile"
      >
        {avatarInner}
      </Button>

      <BottomSheet
        isOpen={userSheetOpen}
        onClose={closeUserSheet}
        ariaLabel={t('labels.user_menu')}
      >
        <div className="flex flex-col gap-1">
          <div className="px-3 py-2">
            <p className="text-xs text-base-content/60">{t('header.signed_in_as')}</p>
            <p className="font-medium truncate">{user?.email}</p>
          </div>
          <div className="border-t border-base-200 my-1" aria-hidden="true" />
          {isAdmin && (
            <Button
              as={Link}
              to="/settings"
              variant="ghost"
              block
              className="justify-start"
              onClick={closeUserSheet}
            >
              {t('nav.settings')}
            </Button>
          )}
          {showPricingLink && (
            <Button
              as="a"
              href="https://flowrent.io/pricing"
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
              block
              className="justify-start"
              rightIcon={<ExternalLink className="w-4 h-4" />}
              onClick={closeUserSheet}
            >
              {t('header.pricing_link')}
            </Button>
          )}
          {hasMenuLinks && <div className="border-t border-base-200 my-1" aria-hidden="true" />}
          <Button
            type="button"
            variant="ghost"
            block
            className="justify-start text-error"
            leftIcon={<LogOut className="w-4 h-4" />}
            onClick={() => {
              closeUserSheet()
              logout()
            }}
          >
            {t('actions.logout')}
          </Button>
        </div>
      </BottomSheet>
    </header>
  )
}

interface PropertyPickerOption {
  value: string
  label: string
}

interface PropertyPickerListboxProps {
  options: PropertyPickerOption[]
  value: string
  onChange: (value: string) => void
  ariaLabel: string
  className?: string
}

// P6/H9: a bespoke anchored listbox for the header's desktop property picker.
// Select.tsx's native-desktop/custom-mobile split is by-design for every other
// consumer app-wide — this is a standalone control, not a Select variant.
// Reuses Select's `ul role="listbox"` / `li role="option"` markup plus
// SearchableDropdown's outside-click (mousedown listener) and Escape-close
// mechanics, which is what makes the control CDP/headless-clickable.
function PropertyPickerListbox({ options, value, onChange, ariaLabel, className = '' }: PropertyPickerListboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
    }
  }

  const selected = options.find((o) => o.value === value)

  const choose = (v: string) => {
    onChange(v)
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        data-testid="header-property-picker"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen((o) => !o)}
        className="select select-bordered w-full flex items-center justify-between text-left"
      >
        <span className="truncate leading-none">{selected?.label ?? ariaLabel}</span>
        <ChevronDown className="w-4 h-4 shrink-0 opacity-60" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className="absolute z-50 mt-1 w-full rounded-lg border border-base-300 bg-base-100 shadow-lg max-h-60 overflow-auto"
        >
          {options.map((option) => {
            const isSelected = option.value === value
            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => choose(option.value)}
                  className={`flex items-center justify-between w-full px-4 py-2 text-left hover:bg-primary/10 ${
                    isSelected ? 'font-semibold text-primary' : ''
                  }`}
                >
                  <span className="truncate leading-none">{option.label}</span>
                  {isSelected && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
